/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CostInsightsPage.tsx
 *     Feature         :   258-cost-analytics-azure-spend (US1-US5) — reworks the 217 screen
 *     Description     :   Cost Analytics — ACTUAL Azure spend (dbo.azure_cost_daily via Spec 257)
 *                         is the primary content: total + daily trend + breakdowns (subscription /
 *                         service·meter / cost-dimension / resource / resource-group / storage
 *                         account) + a dedicated egress panel ($ next to NAT bytes + route mix).
 *                         Static panels scoped by global date + subscription filters (no per-row
 *                         drill). The modeled SKU estimates (217) are DEMOTED to a labeled
 *                         secondary section. Read-only; entity-first data via the backend.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05  (reworked 2026-07-27, Spec 258)
 * =========================================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { DollarSign, Info } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, StatCard, AppTooltip, ErrorState, LoadingState,
  Extended, QuickGlance, DashboardPanel, Select, EmptyState, Badge, chartTooltipStyle, CostSourceBadge, Alert,
} from '../../components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';
import type { DataTableColumn } from '../../components/ui';
import CostDrilldownModal, { type DrilldownTarget } from './CostDrilldownModal';

// ── payloads ──────────────────────────────────────────────────────────────────
interface AzureCostPayload {
  window: { from: string; to: string; subscriptionId: string | null };
  currency: string | null;
  total: { actualUsd: number | null; rowDays: number };
  bySubscription: Array<{ subscriptionId: string; name: string | null; usd: number }>;
  byService: Array<{ meterCategory: string; serviceName: string | null; usd: number }>;
  byDimension: Array<{ dimension: string; usd: number }>;
  byResourceGroup: Array<{ resourceGroup: string; usd: number }>;
  topResources: Array<{ resourceId: string | null; name: string | null; type: string | null; resourceGroup: string | null; usd: number }>;
  byStorageAccount: Array<{ storageAccountId: string | null; accountName: string | null; usd: number }>;
  dailyTrend: Array<{ usageDate: string; usd: number; byDimension: Record<string, number>; subscriptions: number }>;
  /** Spec 286 T159 item 5: how much of the estate the window's cost covers. */
  coverage: {
    windowDays: number;
    daysWithCost: number;
    daysWithoutCost: number;
    subscriptionsInWindow: number;
    partialDays: number;
    latestDay: { usageDate: string; subscriptions: number } | null;
  };
  egress: {
    dollarsByDay: Array<{ usageDate: string; usd: number }>;
    natBytesByDay: Array<{ usageDate: string; bytesOut: number }>;
    natBytesDays: number;
    routes: Array<{ regionAxis: string; pathAxis: string; bytesEgressed: number; bytesTransferred: number }>;
    routesWindowed: boolean;
  };
  unattributed: { usd: number };
  byCapacityActual: Array<{ capacityId: string; name: string | null; sku: string | null; usd: number }>;
  byClientActual: Array<{ clientId: string; clientName: string | null; usd: number; capacities: number }>;
}

// ── formatters + chart palette (SERIES_COLORS = the sanctioned chart-only hex exception) ──
const DIMENSIONS = ['storage', 'data_transfer', 'compute', 'transactions'] as const;
const SERIES_COLORS: Record<string, string> = {
  storage: '#4f8ff7', data_transfer: '#e5686a', compute: '#f2a541', transactions: '#4fbf9f',
};
const usd = (v: number | null | undefined): string => (v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const fmtBytes = (b: number): string => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']; const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / Math.pow(1024, i)).toFixed(i ? 2 : 0)} ${u[i]}`;
};

// ── date-window presets ─────────────────────────────────────────────────────────
type Preset = '7' | '30' | '90' | 'incident';
const iso = (d: Date) => d.toISOString().slice(0, 10);
function windowFor(preset: Preset): { from: string; to: string } {
  if (preset === 'incident') return { from: '2026-06-01', to: '2026-07-26' };
  const days = Number(preset);
  const to = new Date(Date.now() - 86_400_000);
  const from = new Date(Date.now() - (days + 1) * 86_400_000);
  return { from: iso(from), to: iso(to) };
}
const PRESET_OPTS = [
  { value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' }, { value: 'incident', label: 'NAT incident (Jun–Jul)' },
];

export const CostInsightsPage: React.FC = () => {
  const [preset, setPreset] = React.useState<Preset>('30');
  const [sub, setSub] = React.useState<string>(''); // '' = all
  const [drill, setDrill] = React.useState<DrilldownTarget | null>(null); // clicked-row breakdown
  const { from, to } = windowFor(preset);

  // ACTUAL Azure spend (primary) — re-queries on window/subscription change (FR-005)
  const q = useQuery<AzureCostPayload>({
    queryKey: ['cost-insights-azure', from, to, sub],
    queryFn: async () => {
      const p = new URLSearchParams({ from, to });
      if (sub) p.set('subscriptionId', sub);
      const res = await apiClient.get<{ success: boolean; data: AzureCostPayload }>(`/api/cost-insights/azure?${p.toString()}`);
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const page = usePageData(q);
  const data = page.data;

  useAutoRefresh(page.refresh, { lastFetchedAt: page.lastFetchedAt });

  // A date-only value parsed as UTC midnight would read as the previous day in Eastern time; noon keeps the date.
  const onDay = (d: string) => formatEasternDate(`${d}T12:00:00Z`);

  const subOptions = React.useMemo(() => ([
    { value: '', label: 'All subscriptions' },
    ...(data?.bySubscription ?? [])
      .filter((r) => r.subscriptionId && r.subscriptionId !== 'null')
      .map((r) => ({ value: r.subscriptionId, label: r.name ?? r.subscriptionId })),
  ]), [data?.bySubscription]);

  const subCols: DataTableColumn<AzureCostPayload['bySubscription'][number]>[] = [
    { key: 'name', header: 'Subscription', width: '55%', cellClassName: 'font-medium text-[var(--color-text-primary)]',
      render: (r) => r.name ?? <span className="text-[var(--color-text-tertiary)]" title={r.subscriptionId}>{r.subscriptionId} <span className="text-xs">(name not collected)</span></span> },
    { key: 'usd', header: 'Actual $', width: '45%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const svcCols: DataTableColumn<AzureCostPayload['byService'][number]>[] = [
    { key: 'meterCategory', header: 'Service / meter', width: '55%', render: (r) => <span>{r.meterCategory}{r.serviceName ? <span className="text-[var(--color-text-tertiary)]"> · {r.serviceName}</span> : null}</span> },
    { key: 'usd', header: 'Actual $', width: '45%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const rgCols: DataTableColumn<AzureCostPayload['byResourceGroup'][number]>[] = [
    { key: 'resourceGroup', header: 'Resource group', width: '55%',
      render: (r) => (r.resourceGroup === '(unattributed)'
        ? <AppTooltip title="Cost whose resource isn't inventoried in dbo.azure_resources — e.g. the un-inventoried subscriptions. Not a bug; those resources aren't collected."><span className="text-[var(--color-text-tertiary)]">Un-inventoried resource</span></AppTooltip>
        : r.resourceGroup) },
    { key: 'usd', header: 'Actual $', width: '45%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const resCols: DataTableColumn<AzureCostPayload['topResources'][number]>[] = [
    { key: 'name', header: 'Resource', width: '35%', cellClassName: 'font-medium text-[var(--color-text-primary)]', render: (r) => r.name ?? '(sub-level / un-inventoried)' },
    { key: 'type', header: 'Type', width: '30%', render: (r) => r.type ?? '—' },
    { key: 'resourceGroup', header: 'Resource group', width: '20%', render: (r) => r.resourceGroup ?? '—' },
    { key: 'usd', header: 'Actual $', width: '15%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const saCols: DataTableColumn<AzureCostPayload['byStorageAccount'][number]>[] = [
    { key: 'accountName', header: 'Storage account', width: '55%', render: (r) => r.accountName ?? r.storageAccountId ?? '—' },
    { key: 'usd', header: 'Actual $', width: '45%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const capacityActualCols: DataTableColumn<AzureCostPayload['byCapacityActual'][number]>[] = [
    { key: 'name', header: 'Capacity', width: '45%', cellClassName: 'font-medium text-[var(--color-text-primary)]', render: (r) => r.name ?? '(unnamed)' },
    { key: 'sku', header: 'SKU', width: '20%', render: (r) => r.sku ?? '—' },
    { key: 'usd', header: 'Actual $', width: '35%', align: 'right', render: (r) => usd(r.usd) },
  ];
  const clientActualCols: DataTableColumn<AzureCostPayload['byClientActual'][number]>[] = [
    { key: 'clientName', header: 'Client', width: '50%', cellClassName: 'font-medium text-[var(--color-text-primary)]', render: (r) => r.clientName ?? '(unnamed)' },
    { key: 'capacities', header: 'Capacities', width: '20%', align: 'right', render: (r) => String(r.capacities) },
    { key: 'usd', header: 'Actual $ (allocated)', width: '30%', align: 'right', render: (r) => usd(r.usd) },
  ];

  const openDrill = (t: DrilldownTarget | null) => { if (t && t.key) setDrill(t); };

  const filters = (
    <div className="flex items-center gap-2">
      <Select aria-label="Cost window" value={preset} onChange={(e) => setPreset(e.target.value as Preset)} options={PRESET_OPTS} />
      <Select aria-label="Subscription filter" value={sub} onChange={(e) => setSub(e.target.value)} options={subOptions} />
    </div>
  );

  const hasData = !!data && data.total.actualUsd != null;

  return (
    <>
    <PageLayout
      title="Cost Analytics"
      description="Actual Azure spend, broken down by what we collect"
      icon={<DollarSign className="h-6 w-6" />}
      helpId="insights.cost"
      actions={filters}
      onRefresh={page.refresh}
      isRefreshing={page.isRefreshing}
      lastFetchedAt={page.lastFetchedAt}
      refreshError={page.error}
    >
      {page.error && !data ? (
        <ErrorState title="Failed to load cost analytics" message={(page.error as Error).message} onRetry={page.refresh} />
      ) : page.isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <div className="space-y-6">
          {/* actual-first KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="relative h-full">
              <StatCard className="h-full" label={`Actual Azure spend · ${from} → ${to}`} value={usd(data.total.actualUsd)} />
              <div className="mt-1"><CostSourceBadge source="actual" /></div>
              <div className="absolute right-3 top-3">
                <AppTooltip title="Real Azure billing from dbo.azure_cost_daily (Cost Management), summed over the selected window and subscription. Not an estimate.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <StatCard className="h-full" label="Subscriptions with cost" value={String(data.bySubscription.length)}
              subtext={sub ? 'filtered' : data.coverage.latestDay
                ? `${data.coverage.latestDay.subscriptions} on ${onDay(data.coverage.latestDay.usageDate)}` : undefined} />
            <StatCard className="h-full" label="Days covered" value={String(data.coverage.daysWithCost)}
              subtext={`of ${data.coverage.windowDays} days${data.currency ? ` (${data.currency})` : ''}`} />
          </div>

          {/* Spec 286 T159 item 5: a fall in coverage must not read as a fall in spend. */}
          {(data.coverage.partialDays > 0 || (data.coverage.daysWithoutCost > 0 && data.coverage.daysWithCost > 0)) && (
            <Alert variant="warning" title="The cost covers part of this window" data-testid="cost-coverage-notice">
              {data.coverage.partialDays > 0 && data.coverage.latestDay && (
                <p>
                  {data.coverage.partialDays} of the {data.coverage.daysWithCost} days with cost cover fewer than
                  the {data.coverage.subscriptionsInWindow} subscriptions seen in this window (the latest,{' '}
                  {onDay(data.coverage.latestDay.usageDate)}, covers {data.coverage.latestDay.subscriptions}).
                </p>
              )}
              {data.coverage.daysWithoutCost > 0 && (
                <p>No cost was collected on {data.coverage.daysWithoutCost} of the {data.coverage.windowDays} days.</p>
              )}
              <p className="mt-1">The totals and the trend show only what was collected.</p>
            </Alert>
          )}

          {!hasData ? (
            <EmptyState title="No Azure cost for this period" message="No dbo.azure_cost_daily rows in the selected window. Widen the window, or (on prod) confirm the cost backfill has run." />
          ) : (
            <>
              <QuickGlance>
                <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
                  Actual spend {usd(data.total.actualUsd)} across {data.bySubscription.length} subscription(s). Open on a larger screen for the full breakdown, trend, and egress detail.
                </Card>
              </QuickGlance>

              <Extended>
                <div className="space-y-6">
                  {/* daily trend, stacked by dimension */}
                  <DashboardPanel title="Daily spend trend" subtitle="Actual Azure cost per day, stacked by cost dimension — the egress spike is the red band.">
                    <div className="h-72 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.dailyTrend.map((d) => ({ usageDate: d.usageDate, subscriptions: d.subscriptions, ...d.byDimension }))} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                          <XAxis dataKey="usageDate" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                          <Tooltip {...chartTooltipStyle} formatter={(v, name) => [usd(Number(v)), String(name)]}
                            labelFormatter={(label, items) => {
                              const n = (items?.[0]?.payload as { subscriptions?: number } | undefined)?.subscriptions;
                              return n != null ? `${label}: ${n} subscription${n === 1 ? '' : 's'}` : String(label);
                            }} />
                          <Legend />
                          {DIMENSIONS.map((dim) => (
                            <Area key={dim} type="monotone" dataKey={dim} stackId="1" stroke={SERIES_COLORS[dim]} fill={SERIES_COLORS[dim]} fillOpacity={0.55} />
                          ))}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </DashboardPanel>

                  {/* dedicated egress panel (FR-012): $ + NAT bytes + route mix */}
                  <DashboardPanel
                    title="Egress & NAT"
                    subtitle="The data-transfer / NAT-gateway cost that drove the incident — dollars next to bytes and the backup route mix."
                  >
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Egress cost / day (data_transfer + NAT)</p>
                        <div className="h-56 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.egress.dollarsByDay} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                              <XAxis dataKey="usageDate" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                              <Tooltip {...chartTooltipStyle} formatter={(v) => [usd(Number(v)), 'Egress $']} />
                              <Bar dataKey="usd" fill={SERIES_COLORS.data_transfer} radius={[3, 3, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div>
                        <p className="mb-1 text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">NAT gateway bytes out / day (fleet)</p>
                        {data.egress.natBytesDays < data.total.rowDays && (
                          <p className="mb-1 text-xs text-[var(--color-text-secondary)]">
                            Only {data.egress.natBytesDays} day(s) of NAT-byte telemetry so far — hourly NAT collection is recent, so this chart trails the egress-dollars chart (which covers the full window from billing).
                          </p>
                        )}
                        <div className="h-56 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.egress.natBytesByDay} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                              <XAxis dataKey="usageDate" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtBytes(Number(v))} />
                              <Tooltip {...chartTooltipStyle} formatter={(v) => [fmtBytes(Number(v)), 'NAT bytes out']} />
                              <Bar dataKey="bytesOut" fill={SERIES_COLORS.compute} radius={[3, 3, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="mb-2 text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">
                        Backup route mix {data.egress.routesWindowed ? '' : '(all backup runs — route view is not date-windowed)'}
                      </p>
                      {data.egress.routes.length === 0 ? (
                        <p className="text-sm text-[var(--color-text-secondary)]">No classified backup-egress routes.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {data.egress.routes.map((rt, i) => (
                            <Badge key={i} variant={rt.pathAxis === 'nat-internet' ? 'danger' : rt.pathAxis === 'server-side' ? 'success' : 'default'}>
                              {rt.regionAxis} · {rt.pathAxis}: {fmtBytes(rt.bytesEgressed)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </DashboardPanel>

                  {/* REAL per-capacity + per-client spend (from billing joined to the entities) */}
                  {((data.byCapacityActual?.length ?? 0) > 0 || (data.byClientActual?.length ?? 0) > 0) && (
                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                      <Card padding="none">
                        <div className="flex items-center gap-2 px-4 pt-4">
                          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Capacity spend (actual)</h2>
                          <AppTooltip title="Real Azure billing for Fabric capacities that carry an azure_resource_id, joined from dbo.azure_cost_daily. Capacities without a billing link fall back to the modeled estimate below.">
                            <span><Info className="h-3.5 w-3.5 text-[var(--color-text-tertiary)]" /></span>
                          </AppTooltip>
                        </div>
                        <DataTable columns={capacityActualCols} rows={data.byCapacityActual ?? []} rowKey={(r) => r.capacityId} pageSize={10} emptyMessage="No billed capacities in this window" />
                      </Card>
                      <Card padding="none">
                        <div className="flex items-center gap-2 px-4 pt-4">
                          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Per-client spend (actual)</h2>
                          <AppTooltip title="Each billed capacity's real cost allocated across the clients on it by Fabric item count. Only explicitly client-assigned workspaces are attributed; shared / serves-all items stay in the capacity remainder.">
                            <span><Info className="h-3.5 w-3.5 text-[var(--color-text-tertiary)]" /></span>
                          </AppTooltip>
                        </div>
                        <DataTable columns={clientActualCols} rows={data.byClientActual ?? []} rowKey={(r) => r.clientId} pageSize={10} emptyMessage="No client-attributed capacity spend in this window" />
                      </Card>
                    </div>
                  )}

                  {/* breakdown tables (scoped by the global filters; click any row to drill down) */}
                  <p className="text-xs text-[var(--color-text-tertiary)]">Click any row below to break its spend down further.</p>
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Spend by subscription</h2>
                      <DataTable columns={subCols} rows={data.bySubscription} rowKey={(r) => r.subscriptionId} pageSize={10} emptyMessage="No cost in this window"
                        onRowClick={(r) => openDrill({ dimension: 'subscription', key: r.subscriptionId, label: r.name ?? r.subscriptionId })} />
                    </Card>
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Spend by service / meter</h2>
                      <DataTable columns={svcCols} rows={data.byService} rowKey={(r) => `${r.meterCategory}|${r.serviceName ?? ''}`} pageSize={10} emptyMessage="No cost in this window"
                        onRowClick={(r) => openDrill({ dimension: 'service', key: r.meterCategory, label: r.meterCategory })} />
                    </Card>
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Top resources</h2>
                      <DataTable columns={resCols} rows={data.topResources} rowKey={(r) => r.resourceId ?? r.name ?? Math.random().toString()} pageSize={10} emptyMessage="No resource-attributed cost"
                        onRowClick={(r) => openDrill(r.resourceId ? { dimension: 'resource', key: r.resourceId, label: r.name ?? r.resourceId } : null)} />
                    </Card>
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Spend by resource group</h2>
                      <DataTable columns={rgCols} rows={data.byResourceGroup} rowKey={(r) => r.resourceGroup} pageSize={10} emptyMessage="No cost in this window"
                        onRowClick={(r) => openDrill({ dimension: 'resourceGroup', key: r.resourceGroup, label: r.resourceGroup })} />
                    </Card>
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Spend by storage account</h2>
                      <DataTable columns={saCols} rows={data.byStorageAccount} rowKey={(r) => r.storageAccountId ?? 'unattributed'} pageSize={10} emptyMessage="No storage cost in this window"
                        onRowClick={(r) => openDrill(r.storageAccountId ? { dimension: 'storageAccount', key: r.storageAccountId, label: r.accountName ?? r.storageAccountId } : null)} />
                    </Card>
                    <Card padding="none">
                      <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Spend by cost dimension</h2>
                      <DataTable
                        columns={[
                          { key: 'dimension', header: 'Dimension', width: '55%', render: (r) => r.dimension },
                          { key: 'usd', header: 'Actual $', width: '45%', align: 'right', render: (r) => usd(r.usd) },
                        ] as DataTableColumn<AzureCostPayload['byDimension'][number]>[]}
                        rows={data.byDimension} rowKey={(r) => r.dimension} pageSize={10} emptyMessage="No cost in this window"
                        onRowClick={(r) => openDrill({ dimension: 'costDimension', key: r.dimension, label: r.dimension })} />
                    </Card>
                  </div>
                </div>
              </Extended>
            </>
          )}

        </div>
      )}
    </PageLayout>
    <CostDrilldownModal target={drill} window={{ from, to, subscriptionId: sub || null }} onClose={() => setDrill(null)} />
    </>
  );
};

export default CostInsightsPage;
